import React from 'react';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import { VisualSelectOption } from '../../../../constants/clients/template-config.interface';

interface ConfigFieldProps {
  keyName: string;
  value: any;
  getFieldLabel: (key: string) => string;
  formatValue: (value: any) => React.ReactNode;
  background?: string;
}

interface VideoValue {
  originalUrl: string;
}

type InputValue = VisualSelectOption | VideoValue | string | number | boolean | null | undefined;

type NormalizedValue = object | string | number | boolean | null | undefined;

/**
 * TYPE: 'visual-select',
 * Normaliza el valor para mostrar:
 * - Si es objeto con originalUrl (video) → mantener objeto
 * - Si es objeto con value/key (opción de select) → extraer value
 * - Si es string → mantener string
 */
const normalizeValue = (value: InputValue): NormalizedValue => {
  // Si es null/undefined, retornar as-is
  if (value === null || value === undefined) {
    return value;
  }

  // Si no es objeto, retornar as-is (string, number, etc.)
  if (typeof value !== 'object') {
    return value;
  }

  // Si tiene originalUrl, es un video → mantener el objeto completo
  if ('originalUrl' in value) {
    return value;
  }

  if ('imageUrl' in value) {
    return value.imageUrl;
  }

  // Si tiene value (opción de select) → extraer solo el value
  if ('value' in value) {
    return value.value;
  }

  if ('text' in value && 'html' in value) {
    return value;
  }

  // Fallback: convertir a string
  return JSON.stringify(value);
};

export const ConfigField: React.FC<ConfigFieldProps> = ({
  keyName,
  value,
  getFieldLabel,
  formatValue,
  background = 'white'
}) => {
  const { fontSize, spacing } = useResponsiveSizes();

  // Normalizar el valor para display
  const displayValue = normalizeValue(value);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: `${spacing.xs}px ${spacing.sm}px`,
        background,
        borderRadius: 6,
        fontSize: fontSize.sm
      }}
    >
      <span style={{ color: '#64748b', fontWeight: 500 }}>{getFieldLabel(keyName)}</span>
      <span style={{ color: '#0f172a', fontWeight: 600, textAlign: 'right' }}>{formatValue(displayValue)}</span>
    </div>
  );
};
